import React from 'react';
import {
  Button,
  Dropdown,
  Menu,
  Modal,
  Row,
  Tooltip,
} from 'antd';
import { DeleteOutlined, EditOutlined, EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DataGrid,
  HeaderTitle,
  Label,
  Loading,
  ScreenMask,
  SecondaryButton,
  SectionWrapper,
  message,
} from "components/common";
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  //COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  HTTP_STATUS_OK,
  LOADING_ICON_SIZE1,
} from 'constants/config';
import { deletePatternCode, fetchPatternCodeList } from 'services/inventory';
import PatternCodeItemDialog from './PatternCodeItemDialog';

const PatternCode = () => {
  const { useState } = React;
  const [currentPatternCode, setCurrentPatternCode] = useState<StringKAnyVPair>();
  const [inited, setInited] = useState(false);
  const [itemAction, setItemAction] = useState('');
  const [lockScreen, setLockScreen] = useState(false);
  const [patternCodeItemDialogVisible, setPatternCodeItemDialogVisible] = useState(false);
  const [patternCodeList, setPatternCodeList] = useState<StringKAnyVPair[]>([]);

  const columns =  [
    {
      header: 'Name',
      maxWidth: 180,
      name: 'name',
      showColumnMenuTool: false,
      //sortable: false,
    },
    {
      header: 'Enable',
      maxWidth: 70,
      name: 'status',
      showColumnMenuTool: false,
      sortable: false,
      render(row: any) {
        const { data } = row;

        return data.status === 1 ? 'Yes' : <span className="text-danger">No</span>;
      },
    },
    {
      header: 'Note',
      maxWidth: 180,
      name: 'note',
      showColumnMenuTool: false,
      sortable: false,
    },
    {
      header: 'Pattern Code',
      name: 'items',
      showColumnMenuTool: false,
      sortable: false,
      render(row: any) {
        const { data: { items } } = row;

        //return items.map((e: any) => isPatternCodeItemDelimiter(e) ? <span className="text-info fw-600" key={`pci-${e.productCodePatternItemNum}`}>{e.rule}</span> : <Tooltip key={`pci-${e.productCodePatternItemNum}`} title={e.valueType}>({e.rule})</Tooltip>);
        return items.map((e: any) => <Tooltip key={`pci-${e.productCodePatternItemNum}`} title={e.valueType}><span className="bg-item-1 br-5 m-0-1 p-0-1-1">{e.rule}</span></Tooltip>);
      },
    },
    {
      header: 'Action',
      headerAlign: 'start',
      maxWidth: 120,
      showColumnMenuTool: false,
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return (<div className="ant-input-group-wrapper w-none">
          <SecondaryButton
            onClick={() => openPatternCodeItemDialog('Edit', data)}
            size="small"
          >
            <EditOutlined />
            Edit
          </SecondaryButton>
          <Dropdown
            overlay={(
              <Menu>
                <Menu.Item key="rm-batch-btn" onClick={() => confirmTodeletePattern(data)}>
                  <Label className="label-danger">
                    <DeleteOutlined />
                    &nbsp;Delete
                  </Label>
                </Menu.Item>
              </Menu>
            )}
          >
            <Button size="small">
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        </div>);
      },
    },
  ];

  const closePatternCodeItemDialog = () => {
    setPatternCodeItemDialogVisible(false);
  };

  const confirmTodeletePattern = (ptn: StringKAnyVPair) => {
    Modal.confirm({
      title: 'Confirm',
      content: `Do you want to delete this pattern(${ptn.name})?`,
      onOk() {
        deletePattern(ptn);
      },
    });
  };

  const deletePattern = async (ptn: StringKAnyVPair) => {
    const idx = patternCodeList.findIndex(e => e.productCodePatternNum === ptn.productCodePatternNum);

    //console.log('del', idx, ptn);
    if (idx < 0) return;

    setLockScreen(true);
    try {
      const { code, isSuccess } = await deletePatternCode(ptn.productCodePatternNum) as any;
      //console.log('res', code, isSuccess);
      if (code === HTTP_STATUS_OK && isSuccess) {
        patternCodeList.splice(idx, 1);
        setPatternCodeList([ ...patternCodeList ]);
      }
    } catch(e) {
      message.error({
        content: `Deleted pattern code error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const loadPatternCodeList = async () => {
    setLockScreen(true);
    try {
      const { code, data } = await fetchPatternCodeList() as any;

      if (code === HTTP_STATUS_OK && Array.isArray(data)) {
        //console.log(data);
        setPatternCodeList(data);
      }
    } catch(e) {
      message.error({
        content: `Loaded pattern code list error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const openPatternCodeItemDialog = (action: string, patternCode?: StringKAnyVPair) => {
    setItemAction(action);
    setCurrentPatternCode(patternCode);
    setPatternCodeItemDialogVisible(true);
  };

  React.useEffect(() => {
    if (!inited) {
      loadPatternCodeList();
      setInited(true);
    }
  }, [inited]);

  return (<>
    <HeaderTitle breadcrumb={['Administration', 'Multi-purpose Product Code']} />
    <SectionWrapper>
      <Row justify="end">
        <Button
          onClick={() => openPatternCodeItemDialog('Add')}
        >
          <PlusOutlined />
          Add
        </Button>
      </Row>
      <div className="mt-12">
        <DataGrid
          columns={columns}
          dataSource={patternCodeList}
          //loading={}
          rowKey="productCodePatternNum"
          pagination={false}
        />
      </div>
    </SectionWrapper>
    {patternCodeItemDialogVisible && (
      <PatternCodeItemDialog
        maskClosable={false}
        mode={itemAction}
        onCancel={closePatternCodeItemDialog}
        onSaved={loadPatternCodeList}
        patternCode={currentPatternCode}
        title={`${itemAction} Pattern Code Item`}
        visible={patternCodeItemDialogVisible}
        width={1000}
      />
    )}
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default PatternCode;

export enum PatternCodeMode {
  Standalone,
  Aggregation,
};

export enum PatternCodeType {
  PO = 'PO',
  Location = 'Location',
  SKU = 'SKU',
  LOT = 'LOT',
  UOM = 'UOM',
  Qty = 'Qty',
};

export const PatternModeOptions = [
  {
    label: 'Standalone',
    value: PatternCodeMode.Standalone,
  },
  {
    label: 'Aggregation',
    value: PatternCodeMode.Aggregation,
  },
];

export const PatternTypeOptions = [
  {
    label: 'PO',
    value: PatternCodeType.PO,
  },
  {
    label: 'Location',
    value: PatternCodeType.Location,
  },
  {
    label: 'SKU',
    value: PatternCodeType.SKU,
  },
  {
    label: 'LOT #',
    value: PatternCodeType.LOT,
  },
  {
    label: 'UOM',
    value: PatternCodeType.UOM,
  },
  {
    label: 'Qty',
    value: PatternCodeType.Qty,
  },
  /*{
    label: 'Delimiter',
    value: 'Delimiter',
  },*/
];

export const extractMatchedPatternCode = (patternCodeItems: StringKAnyVPair[], code: string) => {
  const res: StringKAnyVPair[] = [];

  patternCodeItems.forEach(e => {
    const reg = new RegExp(e.rule);
    const m = reg.exec(code);

    //console.log('mr', m);
    if (Array.isArray(m) && typeof m[1] === 'string') {
      if (e.valueMode === PatternCodeMode.Aggregation) {
        //console.log('mr', e, m);
        if (e.aggregationRule && typeof e.aggregationRule === 'string') {
          // number is not allowed to be a part of the delimiter
          const ps = e.aggregationRule.trim().match(/\d+|\D+/g);

          if (ps && Array.isArray(ps)) {
            // the index of the aggregation must start from 1
            const nps = ps.filter(pe => /\d+/.test(pe)).map(pe => Number(pe));
            //console.log('nps', nps);
            if (nps.length > 0 && nps.every((idx: any) => m[idx])) {
              const vals: string[] = [];
              //console.log('ps', ps);
              ps.forEach((idx: any) => /\d+/.test(idx) ? vals.push(m[idx]) : vals.push(idx));
              //console.log('pv', vals);
              (vals.length === ps.length) && res.push({result: 0, type: e.valueType, value: vals.join('')});
            }
          }
        }
      } else {
        res.push({result: 0, type: e.valueType, value: m[1]});
      }
    } else {
      res.push({result: 1, type: e.valueType, value: 'Not Match'});
    }
  });

  return res;
};

export const extractPatternCodeFromMatchedResult = (matchedList: string[], patternCodeItems: StringKAnyVPair[]) => {
  const codeItems = patternCodeItems.filter(e => !isPatternCodeItemDelimiter(e));
  const ret: StringKAnyVPair[] = [];
  //console.log('c->', codeItems);
  if (codeItems.length + 1 === matchedList.length) {
    codeItems.forEach((e, i) => {
      const matchedValue = matchedList[i + 1];

      if (matchedValue) ret.push({type: e.valueType, value: matchedValue});
    });
  }

  return ret;
};

export const getPatternRegularExpression = (items: StringKAnyVPair[]) => {
  return items.map(e => isPatternCodeItemDelimiter(e) ? e.rule : `(${e.rule})`).join('');
};

export const isPatternCodeItemDelimiter = (item: StringKAnyVPair) => {
  return item.valueType === 'Delimiter';
};
